import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import User from "@insite/mobius/Icons/User";
import Menu from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Popover, { PopoverPresentationProps } from "@insite/mobius/Popover";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import SideMenu, { SideMenuLinkPresentationProps, SideMenuStyles } from "@store/Components/Common/SideMenu";
import ChevronDown from "@store/Components/Icons/ChevronDown";
import PersonFill from "@store/Components/Icons/Person/PersonFill";
import { PAGE_TYPES } from "@store/constants";
import useMergeProps from "@store/Hooks/Common/useMergeProps";
import { TYPOGRAPHY } from "@store/theme/design";
import { sideMenuLinkActiveStyles, sideMenuLinkStyles } from "@store/theme/mixins";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import linkTo from "@store/utilities/linkTo";
/* Valtech Custom Code */

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    // Valtech Custom Code
    firstName: state.context.session.firstName,
    isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
    allowCreateAccount: state.context.settings.settingsCollection.accountSettings.allowCreateAccount,
    createAccountUrl: getPageLinkByPageType(state, "CreateAccountPage")?.url,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // Valtech Custom Code
    titleTypographyPipe?: TypographyPresentationProps;
    titleSubTypography?: TypographyPresentationProps;
    titleTypographyIcon?: IconPresentationProps;
    popover?: PopoverPresentationProps;
    signedInWrapper?: InjectableCss;
    signedOutWrapper?: InjectableCss;
    signInButton?: ButtonPresentationProps;
    createAccountWrapper?: InjectableCss;
    createAccountTitle?: TypographyPresentationProps;
    createAccountText?: TypographyPresentationProps;
    createAccountLink?: LinkPresentationProps;
    sideMenu?: SideMenuStyles;
    titleClickableLoginOnly?: ClickablePresentationProps;
}

const _linkStyles = () => css<SideMenuLinkPresentationProps>`
    ${sideMenuLinkStyles()}

    ${({ isCurrent }) =>
        isCurrent &&
        css`
            ${sideMenuLinkActiveStyles()}
        `}
`;

export const headerSignInStyles: HeaderSignInStyles = {
    titleClickable: {
        css: css`
            color: inherit;
        `,
    },
    titleClickableLoginOnly: {
        css: css`
            color: inherit;
        `,
    },
    titleIcon: {
        color: "inherit",
    },
    titleTypography: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    titleTypographyPipe: {
        css: css`
            margin-left: 0.5em;
        `,
    },
    titleSubTypography: {
        weight: TYPOGRAPHY.weight.bold,
    },
    titleTypographyIcon: {
        color: "inherit",
        src: ChevronDown,
        css: css`
            margin-left: 0.25em;
        `,
    },
    popover: {
        contentBodyProps: {
            css: css`
                max-height: none;
                color: ${getColor("text")};
                box-shadow: ${props => props.theme.shadows?.light80};
            `,
        },
    },
    sideMenu: {
        wrapper: {},
        link: {
            css: css`
                ${_linkStyles()}
                border: none;
            `,
        },
    },
    signedOutWrapper: {
        css: css`
            text-align: center;
            padding: 2em 1em;
            font-size: ${TYPOGRAPHY.size.smaller};
        `,
    },
    signInButton: {
        variant: "secondary",
        css: css`
            ${props => props.theme?.button?.secondary?.css}
            margin-bottom: 26px;
        `,
    },
    createAccountTitle: {
        size: 16,
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            display: block;
            margin-bottom: 10px;
        `,
    },
    createAccountText: {
        css: css`
            display: block;
            margin-bottom: 1em;
        `,
    },
    createAccountLink: {
        typographyProps: {
            weight: TYPOGRAPHY.weight.bold,
            css: css`
                text-decoration: underline;
            `,
        },
    },
};

/* Valtech Custom Code */
export interface HeaderSignInProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
    };
    // Valtech Custom Code
    extendedStyles?: HeaderSignInStyles;
}

type OwnProps = HeaderSignInProps & WidgetProps;
/* Valtech Custom Code */

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    // Valtech Custom Code
    extendedStyles,
    firstName,
    isSignedIn,
    allowCreateAccount,
    createAccountUrl,
}: Props) => {
    /* Valtech Custom Code */
    const element = React.useRef<HTMLUListElement>(null);
    const popover = React.useRef<HTMLUListElement>(null);
    /* Valtech Custom Code */

    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";

    if (fields.icon === "User") {
        icon = PersonFill; // Valtech Custom Code
    }

    // const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    const styles = useMergeProps(headerSignInStyles, extendedStyles); // Valtech Custom Code
    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = myAccountPageLink?.children;
    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
            e.preventDefault();
            closePopover(); // Valtech Custom Code
        }
    };

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    /* Valtech Custom Code */
    const closePopover = () => {
        popover.current && (popover.current! as any).closePopover();
    };

    const onCreateAccountHandler = () => {
        linkTo(history, createAccountUrl);
        closePopover();
    };

    let signInStatusText = translate("Login");
    let signInStatusSubText = translate("Register");

    if (isSignedIn) {
        signInStatusText = `${translate("Hi")} ${firstName || userName}`;
        signInStatusSubText = translate("Your Account");
    }

    const loginButton = (
        <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
            {showIcon && <Icon {...styles.titleIcon} src={icon} />}
            {showLabel && (
                <Typography {...styles.titleTypography}>
                    {signInStatusText}
                    {!isSignedIn && allowCreateAccount ? (
                        <Typography {...styles.titleTypographyPipe}>|</Typography>
                    ) : null}
                    <Typography {...styles.titleSubTypography}>{signInStatusSubText}</Typography>
                    <Icon {...styles.titleTypographyIcon} />
                </Typography>
            )}
        </Clickable>
    );

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {isSignedIn || allowCreateAccount ? (
                <Popover
                    {...styles.popover}
                    contentBodyProps={{
                        ...styles.popover?.contentBodyProps,
                        as: "div",
                        ref: element,
                        css: css`
                            ${styles.popover?.contentBodyProps?.css}
                            width: 100%;
                            min-width: 220px;
                            ${props =>
                                breakpointValuesMediaQueries(
                                    props.theme,
                                    [{ values: [null, null, isSignedIn ? null : "285px"], properties: ["width"] }],
                                    mediaOptions.min,
                                )}
                        `,
                    }}
                    positionFunction={() => ({
                        position: "absolute",
                        right: 0,
                    })}
                    ref={popover}
                    insideRefs={[element]}
                    popoverTrigger={loginButton}
                >
                    {isSignedIn ? (
                        <StyledWrapper {...styles.signedInWrapper}>
                            <SideMenu
                                pageType={PAGE_TYPES.myAccount}
                                showRootPage
                                showSignOut
                                onClick={closePopover}
                                overridedStyles={styles.sideMenu}
                            />
                        </StyledWrapper>
                    ) : (
                        <StyledWrapper {...styles.signedOutWrapper}>
                            <Button {...styles.signInButton} onClick={onSignInHandler}>
                                {translate("Sign In")}
                            </Button>
                            {allowCreateAccount && (
                                <StyledWrapper {...styles.createAccountWrapper}>
                                    <Typography {...styles.createAccountTitle}>
                                        {translate("New Customer ?")}
                                    </Typography>
                                    <Typography {...styles.createAccountText}>
                                        {siteMessage("headerSignIn_createAccount")}
                                    </Typography>
                                    <Link {...styles.createAccountLink} onClick={onCreateAccountHandler}>
                                        {translate("Create Account")}
                                    </Link>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    )}
                </Popover>
            ) : (
                <Clickable
                    {...styles.titleClickableLoginOnly}
                    onClick={onSignInHandler}
                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                    data-test-selector="header_signIn"
                >
                    {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                    {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
                </Clickable>
            )}
        </StyledWrapper>
    );
    /* Valtech Custom Code */
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
